import { createI18n } from 'vue-i18n';
import zhCN from './locales/zh-CN';
import jaJP from './locales/ja-JP';
import enUS from './locales/en-US';
import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from './types';

export { LOCALE_LABELS, SUPPORTED_LOCALES };
export type { SupportedLocale };

export const STORAGE_LOCALE_KEY = 'app.locale';
export const COOKIE_LOCALE_KEY = 'locale';
/** Product default: Japanese only while locale switching is hidden. */
export const DEFAULT_LOCALE: SupportedLocale = 'ja-JP';
/** Feature gate for re-enabling LanguageSwitcher later. */
export const LOCALE_SWITCHING_ENABLED = false;

const messages = {
  'zh-CN': zhCN,
  'ja-JP': jaJP,
  'en-US': enUS,
};

export function normalizeLocale(input?: string | null): SupportedLocale {
  if (!input) return DEFAULT_LOCALE;
  const normalized = input.trim().replace('_', '-').toLowerCase();
  if (normalized === 'zh' || normalized.startsWith('zh-')) return 'zh-CN';
  if (normalized === 'ja' || normalized.startsWith('ja-')) return 'ja-JP';
  if (normalized === 'en' || normalized.startsWith('en-')) return 'en-US';
  return DEFAULT_LOCALE;
}

export function detectBrowserLocale(languages?: readonly string[]): SupportedLocale {
  if (!LOCALE_SWITCHING_ENABLED) return DEFAULT_LOCALE;

  const candidates =
    languages && languages.length
      ? languages
      : typeof navigator !== 'undefined'
        ? navigator.languages?.length
          ? navigator.languages
          : [navigator.language]
        : [];

  for (const language of candidates) {
    const locale = normalizeLocale(language);
    if (locale !== DEFAULT_LOCALE || language.toLowerCase().startsWith('ja')) {
      return locale;
    }
  }
  return DEFAULT_LOCALE;
}

function readCookie(name: string) {
  if (typeof document === 'undefined') return '';
  const prefix = `${encodeURIComponent(name)}=`;
  const entry = document.cookie.split('; ').find((value) => value.startsWith(prefix));
  return entry ? decodeURIComponent(entry.slice(prefix.length)) : '';
}

function writeLocaleCookie(locale: SupportedLocale) {
  if (typeof document === 'undefined') return;
  const maxAge = 60 * 60 * 24 * 365;
  document.cookie = `${COOKIE_LOCALE_KEY}=${encodeURIComponent(locale)}; Max-Age=${maxAge}; path=/; SameSite=Lax`;
}

export function getSavedLocale(): SupportedLocale | null {
  if (!LOCALE_SWITCHING_ENABLED) return null;
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_LOCALE_KEY);
    if (saved) return normalizeLocale(saved);
  }
  const cookieLocale = readCookie(COOKIE_LOCALE_KEY);
  return cookieLocale ? normalizeLocale(cookieLocale) : null;
}

function initialLocale(): SupportedLocale {
  if (!LOCALE_SWITCHING_ENABLED) return DEFAULT_LOCALE;
  return getSavedLocale() || detectBrowserLocale();
}

export const i18n = createI18n({
  legacy: false,
  locale: initialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages,
  datetimeFormats: {
    'zh-CN': {
      short: { year: 'numeric', month: '2-digit', day: '2-digit' },
      medium: { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' },
      long: { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' },
    },
    'ja-JP': {
      short: { year: 'numeric', month: '2-digit', day: '2-digit' },
      medium: { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' },
      long: { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' },
    },
    'en-US': {
      short: { year: 'numeric', month: 'short', day: '2-digit' },
      medium: { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' },
      long: { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' },
    },
  },
  numberFormats: {
    'zh-CN': { currency: { style: 'currency', currency: 'CNY' } },
    'ja-JP': { currency: { style: 'currency', currency: 'JPY' } },
    'en-US': { currency: { style: 'currency', currency: 'USD' } },
  },
});

export function getCurrentLocale(): SupportedLocale {
  return normalizeLocale(i18n.global.locale.value);
}

export function setLocale(locale: SupportedLocale) {
  const nextLocale = normalizeLocale(locale);
  i18n.global.locale.value = nextLocale;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_LOCALE_KEY, nextLocale);
  }
  writeLocaleCookie(nextLocale);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = nextLocale;
  }
}

export function getLocaleHeaders() {
  // Always advertise Japanese while product locale switching is disabled.
  const locale = LOCALE_SWITCHING_ENABLED ? getCurrentLocale() : DEFAULT_LOCALE;
  return {
    'Accept-Language': locale,
    'X-Locale': locale,
  };
}

// Force Japanese on boot so stale saved zh/en prefs do not surface in the UI.
setLocale(DEFAULT_LOCALE);
